import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { FinalCTA } from "@/components/landing/FinalCTA";

/* Shared editorial wrapper used by Privacy / Terms / Security pages.
   - Dark cover-style hero matching /why-clinicfirst
   - Sticky table-of-contents on the left, document body on the right
   - sections: [{ id, title, body: ReactNode }] */

export const LegalPage = ({
  kicker,
  title,
  highlight,
  subtitle,
  updated,
  sections,
  testId,
}) => {
  return (
    <article data-testid={testId} className="overflow-hidden">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a0a14] text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-45"
          style={{ background: "radial-gradient(55% 70% at 75% 0%, rgba(124,58,237,0.25), transparent 70%), radial-gradient(45% 55% at 15% 80%, rgba(14,165,233,0.16), transparent 70%)" }}
        />
        <div className="relative mx-auto max-w-5xl px-6 pt-32 pb-16 sm:px-10 sm:pt-40 sm:pb-20 lg:px-16">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-white/60">
            {kicker}
          </p>
          <h1 className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            {title}{" "}
            {highlight && (
              <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-rose-400 bg-clip-text italic text-transparent">
                {highlight}
              </span>
            )}
          </h1>
          {subtitle && (
            <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-white/70 sm:text-base">
              {subtitle}
            </p>
          )}
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/40">
            Last updated · {updated}
          </p>
        </div>
      </section>

      {/* BODY */}
      <section className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto grid max-w-5xl gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:gap-14 lg:px-16">
          {/* TOC */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
                On this page
              </p>
              <ol className="mt-4 space-y-2.5 border-l border-border pl-5">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`legal-toc-${s.id}`}
                      className="group inline-flex items-start gap-2 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/60">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="leading-snug">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <Link
                to="/"
                className="mt-8 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.22em] text-violet-600 hover:text-violet-700 dark:text-violet-300"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to home
              </Link>
            </div>
          </aside>

          {/* CONTENT */}
          <div className="lg:col-span-8">
            <div className="space-y-12">
              {sections.map((s, i) => (
                <motion.section
                  key={s.id}
                  id={s.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.35, delay: i * 0.03 }}
                  className="scroll-mt-28"
                >
                  <span className="font-mono text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-400">
                    §{String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-1.5 font-display text-2xl font-black leading-tight tracking-tight sm:text-[28px]">
                    {s.title}
                  </h2>
                  <div className="prose prose-sm mt-4 max-w-none text-[15px] leading-[1.75] text-foreground/80 [&>p]:mt-4 [&>p:first-child]:mt-0 [&>ul]:mt-4 [&>ul]:list-disc [&>ul]:space-y-2 [&>ul]:pl-5 [&_strong]:font-semibold [&_strong]:text-foreground">
                    {s.body}
                  </div>
                </motion.section>
              ))}
            </div>

            <div className="mt-16 flex items-center justify-between border-t border-border pt-6 text-[13px] text-muted-foreground">
              <p>Questions about this page?</p>
              <Link
                to="/support"
                className="inline-flex items-center gap-1 font-bold text-violet-600 hover:text-violet-700 dark:text-violet-300"
              >
                Contact support <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </article>
  );
};
